import { useState, useEffect } from 'react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { api } from '../../lib/api';
import { useToastStore } from '../../store/toastStore';
import { Plus, Trash2 } from 'lucide-react';

interface Section {
  title: string;
  body_markdown: string;
}

interface Checkpoint {
  id: string;
  label: string;
  description?: string;
}

interface Guideline {
  id: string;
  version: string;
  effective_from: string;
  is_active: boolean;
  created_at: string;
  sections?: Section[];
  checkpoints?: Checkpoint[];
}

const emptySection = (): Section => ({ title: '', body_markdown: '' });
const emptyCheckpoint = (): Checkpoint => ({
  id: crypto.randomUUID(),
  label: '',
  description: '',
});

export function AdminGuidelinesPage() {
  const [guidelines, setGuidelines] = useState<Guideline[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [version, setVersion] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [sections, setSections] = useState<Section[]>([emptySection()]);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([emptyCheckpoint()]);
  const [saving, setSaving] = useState(false);
  const showToast = useToastStore((s) => s.show);

  const fetchGuidelines = async () => {
    try {
      const res = await api.get<{ guidelines: Guideline[] }>('/guidelines');
      setGuidelines(res.guidelines);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchGuidelines(); }, []);

  const resetForm = () => {
    setVersion('');
    setEffectiveFrom('');
    setSections([emptySection()]);
    setCheckpoints([emptyCheckpoint()]);
    setEditingId(null);
    setShowForm(false);
  };

  const loadForEdit = (g: Guideline) => {
    setEditingId(g.id);
    setVersion(g.version);
    setEffectiveFrom(g.effective_from.split('T')[0]);
    setSections(g.sections?.length ? g.sections : [emptySection()]);
    setCheckpoints(g.checkpoints?.length ? g.checkpoints : [emptyCheckpoint()]);
    setShowForm(true);
  };

  const activateGuideline = async (id: string) => {
    await api.patch(`/guidelines/${id}/activate`, {});
    showToast('Guideline activated', 'success');
    fetchGuidelines();
  };

  const saveGuideline = async () => {
    if (!version.trim() || !effectiveFrom) {
      showToast('Version and effective date are required', 'error');
      return;
    }

    const cleanSections = sections.filter((s) => s.title.trim() || s.body_markdown.trim());
    const cleanCheckpoints = checkpoints
      .filter((c) => c.label.trim())
      .map((c) => ({ ...c, id: c.id || crypto.randomUUID() }));

    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/guidelines/${editingId}`, {
          sections: cleanSections,
          checkpoints: cleanCheckpoints,
        });
        showToast('Guideline updated', 'success');
      } else {
        await api.post('/guidelines', {
          version,
          effective_from: effectiveFrom,
          sections: cleanSections,
          checkpoints: cleanCheckpoints,
          is_active: true,
        });
        showToast('Guideline created and activated', 'success');
      }
      resetForm();
      fetchGuidelines();
    } catch (err: any) {
      showToast(err.message || 'Failed to save guideline', 'error');
    }
    setSaving(false);
  };

  const updateSection = (index: number, field: keyof Section, value: string) => {
    setSections((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const updateCheckpoint = (index: number, field: keyof Checkpoint, value: string) => {
    setCheckpoints((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Guidelines Management</h1>
        <Button size="sm" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? 'Cancel' : 'New Version'}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {editingId ? 'Edit Guideline Content' : 'Create New Version'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label>Version</Label>
                <Input
                  value={version}
                  onChange={(e) => setVersion(e.target.value)}
                  placeholder="v1.1.0"
                  disabled={Boolean(editingId)}
                />
              </div>
              <div>
                <Label>Effective From</Label>
                <Input
                  type="date"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  disabled={Boolean(editingId)}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Sections</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setSections((s) => [...s, emptySection()])}>
                  <Plus className="h-3 w-3 mr-1" /> Add Section
                </Button>
              </div>
              {sections.map((section, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <Input
                      placeholder="Section title"
                      value={section.title}
                      onChange={(e) => updateSection(idx, 'title', e.target.value)}
                    />
                    {sections.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setSections((s) => s.filter((_, i) => i !== idx))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <Textarea
                    placeholder="Markdown body"
                    value={section.body_markdown}
                    onChange={(e) => updateSection(idx, 'body_markdown', e.target.value)}
                    rows={4}
                  />
                </div>
              ))}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Checkpoints</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setCheckpoints((c) => [...c, emptyCheckpoint()])}>
                  <Plus className="h-3 w-3 mr-1" /> Add Checkpoint
                </Button>
              </div>
              {checkpoints.map((cp, idx) => (
                <div key={cp.id} className="border rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Checkpoint label"
                      value={cp.label}
                      onChange={(e) => updateCheckpoint(idx, 'label', e.target.value)}
                      className="flex-1"
                    />
                    {checkpoints.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setCheckpoints((c) => c.filter((_, i) => i !== idx))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <Input
                    placeholder="Description (optional)"
                    value={cp.description || ''}
                    onChange={(e) => updateCheckpoint(idx, 'description', e.target.value)}
                  />
                </div>
              ))}
            </div>

            <Button size="sm" onClick={saveGuideline} disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create & Activate'}
            </Button>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : (
        <div className="border rounded-lg divide-y">
          {guidelines.map((g) => (
            <div key={g.id} className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <span className="font-medium">{g.version}</span>
                {g.is_active && <Badge variant="success">Active</Badge>}
                <span className="text-xs text-muted-foreground">
                  {g.sections?.length || 0} sections · {g.checkpoints?.length || 0} checkpoints
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  Effective: {new Date(g.effective_from).toLocaleDateString()}
                </span>
                <Button variant="outline" size="sm" onClick={() => loadForEdit(g)}>
                  Edit
                </Button>
                {!g.is_active && (
                  <Button variant="outline" size="sm" onClick={() => activateGuideline(g.id)}>
                    Activate
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
